// ===========================================
// BUSTED! BLACKJACK - USER MENU COMPONENT
// ===========================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useFeedbackStore } from '../stores';
import { FeedbackModal } from './FeedbackModal';
import { BahtSymbol } from './Currency';
import { formatBalance } from '../utils/format';
import { APP_VERSION } from '../version';

// ===========================================
// TOGGLE SWITCH COMPONENT
// ===========================================

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ enabled, onChange, disabled = false }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-casino-gold-500
        ${enabled ? 'bg-green-500' : 'bg-gray-600'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4 transform rounded-full
          bg-white shadow ring-0 transition duration-200 ease-in-out
          ${enabled ? 'translate-x-4' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

export function UserMenu() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, setShowRecommendations, setAutoPlay } = useAuthStore();
  const { feedbackType, openFeedback, openSupport, close: closeFeedback } = useFeedbackStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdatingPref, setIsUpdatingPref] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle toggle for recommendations
  const handleToggleRecommendations = useCallback(async (enabled: boolean) => {
    setIsUpdatingPref(true);
    await setShowRecommendations(enabled);
    setIsUpdatingPref(false);
  }, [setShowRecommendations]);

  // Handle toggle for auto play
  const handleToggleAutoPlay = useCallback(async (enabled: boolean) => {
    setIsUpdatingPref(true);
    await setAutoPlay(enabled);
    setIsUpdatingPref(false);
  }, [setAutoPlay]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/login');
  };

  if (!isAuthenticated || !user) {
    return (
      <button
        onClick={() => navigate('/login')}
        className="btn-primary"
      >
        Sign In
      </button>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"
      >
        {/* Avatar */}
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName}
            className="w-8 h-8 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold">
            {user.username.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Name and Balance */}
        <div className="text-left hidden sm:block">
          <p className="text-sm font-medium text-white">{user.displayName}</p>
          <p className="text-xs text-casino-gold-400">
            <BahtSymbol />{formatBalance(user.balance)}<sup className="text-[8px] ml-0.5">*</sup>
          </p>
        </div>

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-1 z-[100]">
          {/* Balance (mobile) */}
          <div className="px-4 py-2 border-b border-gray-700 sm:hidden">
            <p className="text-sm text-gray-400">Balance</p>
            <p className="text-lg font-bold text-casino-gold-400">
              <BahtSymbol />{formatBalance(user.balance)}<sup className="text-[10px] ml-0.5">*</sup>
            </p>
          </div>

          {/* Menu Items */}
          <button
            onClick={() => {
              setIsOpen(false);
              // TODO: Navigate to profile
            }}
            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 transition-colors"
          >
            Profile
          </button>

          <button
            onClick={() => {
              setIsOpen(false);
              // TODO: Navigate to locker
            }}
            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 transition-colors"
          >
            My Locker <span className="text-gray-500 text-sm">(coming soon)</span>
          </button>

          {/* Divider */}
          <div className="border-t border-gray-700 my-1"></div>

          {/* Player's Guide */}
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/guide');
            }}
            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 transition-colors"
          >
            Player's Guide
          </button>

          {/* Settings Section - Admin Only */}
          {user.isAdmin && (
            <div className="px-4 py-2 border-b border-gray-700">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Settings</p>

              {/* Play Recommendations Toggle */}
              <div className="flex items-center justify-between py-1">
                <div className="flex-1 mr-3">
                  <p className="text-sm text-gray-300">Play Hints</p>
                  <p className="text-xs text-gray-500">Show optimal strategy</p>
                </div>
                <ToggleSwitch
                  enabled={user.showRecommendations}
                  onChange={handleToggleRecommendations}
                  disabled={isUpdatingPref}
                />
              </div>

              {/* Auto Play Toggle */}
              <div className="flex items-center justify-between py-1">
                <div className="flex-1 mr-3">
                  <p className="text-sm text-gray-300">Auto Play</p>
                  <p className="text-xs text-gray-500">Auto-execute recommendations</p>
                </div>
                <ToggleSwitch
                  enabled={user.autoPlay}
                  onChange={handleToggleAutoPlay}
                  disabled={isUpdatingPref || !user.showRecommendations}
                />
              </div>
            </div>
          )}

          <button
            onClick={() => {
              setIsOpen(false);
              openFeedback();
            }}
            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 transition-colors"
          >
            Feedback
          </button>

          <button
            onClick={() => {
              setIsOpen(false);
              openSupport();
            }}
            className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-700 transition-colors"
          >
            Support
          </button>

          {/* Admin Link */}
          {user.isAdmin && (
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/adminpanel');
              }}
              className="w-full px-4 py-2 text-left text-casino-gold-400 hover:bg-gray-700 transition-colors"
            >
              Admin Panel
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-700 my-1"></div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left text-red-400 hover:bg-gray-700 transition-colors"
          >
            Sign Out
          </button>

          {/* Version */}
          <div className="border-t border-gray-700 mt-1 px-4 py-2">
            <span className="text-gray-300 text-xs">BUSTED Blackjack v{APP_VERSION}</span>
          </div>
        </div>
      )}

      {/* Feedback/Support Modal */}
      {feedbackType && (
        <FeedbackModal type={feedbackType} onClose={closeFeedback} />
      )}
    </div>
  );
}

export default UserMenu;

// === END OF FILE ===